import { Select } from "@nulogy/components";
import React, { useMemo } from "react";
import FilterLabel from "./FilterLabel";

/**
 * Renders a multi-select filter for a single custom work order field.
 *
 * @param {Object} field - The field definition with id, label, and values
 * @param {Array} fieldFilters - Array of { fieldId, valueIds } for all fields
 * @param {Function} onChange - Callback with updated fieldFilters array
 */
export default function CustomWorkOrderFieldSelector({ field, fieldFilters, onChange }) {
  const options = useMemo(() => buildOptions(field.values), [field.values]);

  // Find this field's selected values from the filters array
  const selectedValueIds = useMemo(() => getSelectedValueIdsForField(fieldFilters, field.id), [fieldFilters, field.id]);

  const handleChange = (newSelectedIds) => {
    const updatedFilters = updateFieldFilters(fieldFilters, field.id, newSelectedIds);
    onChange(updatedFilters);
  };

  return (
    <div data-testid={`custom-work-order-field-filter-${field.id}`}>
      <FilterLabel value={field.label} />
      <Select
        className="spec-custom-work-order-field-filter-multiselect"
        closeMenuOnSelect={false}
        maxHeight="250"
        multiselect
        onChange={handleChange}
        options={options}
        placeholder="Select..."
        value={selectedValueIds}
      />
    </div>
  );
}

function buildOptions(values) {
  return values.map(({ id, description, indicator }) => ({
    color: indicator,
    label: description,
    value: id,
  }));
}

// Exported for testing
export function getSelectedValueIdsForField(fieldFilters, fieldId) {
  if (!fieldFilters) return [];
  const entry = fieldFilters.find((f) => f.fieldId === fieldId);
  return entry?.valueIds || [];
}

// Exported for testing
export function updateFieldFilters(fieldFilters, fieldId, newValueIds) {
  const currentFilters = fieldFilters || [];
  const hasValues = newValueIds && newValueIds.length > 0;

  // Remove existing entry for this field
  const otherFilters = currentFilters.filter((f) => f.fieldId !== fieldId);

  if (hasValues) {
    // Add updated entry for this field
    return [...otherFilters, { fieldId, valueIds: newValueIds }];
  }

  // No values selected for this field - return only other filters
  return otherFilters.length > 0 ? otherFilters : [];
}
